import React, { memo, useCallback } from 'react';
import { StyleSheet, View } from 'react-native';

import { useResponsive } from '@garnish/core';

import { PaymentState } from '@sg/shared-fe-features-ordering';

import { type PaymentMethod } from '@order/features-ordering';

import {
  type GiftCardCheckoutFormFieldRules,
  type GiftCardCheckoutFormReturn,
} from '../../../../form';
import { useGiftCardStripeIntegration } from '../../../../hooks/useGiftCardStripeIntegration';
import { GiftCardCheckoutAmount } from '../../sections/GiftCardCheckoutAmount';
import { GiftCardCheckoutDeliveryDate } from '../../sections/GiftCardCheckoutDeliveryDate';
import { GiftCardCheckoutDeliveryMethod } from '../../sections/GiftCardCheckoutDeliveryMethod';
import { GiftCardCheckoutDetails } from '../../sections/GiftCardCheckoutDetails';
import { GiftCardCheckoutLedger } from '../../sections/GiftCardCheckoutLedger';
import { GiftCardCheckoutPaymentElement } from '../../sections/GiftCardCheckoutPaymentElement';
import { GiftCardCheckoutSubmit } from '../../sections/GiftCardCheckoutSubmit';
import { GiftCardCheckoutFormContainer } from '../GiftCardCheckoutFormContainer';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardCheckoutFormContent = memo((props: GiftCardCheckoutFormContentProps) => {
  const {
    amount,
    paymentMethods,
    sectionTitles,
    form,
    rules,
    canSubmit,
    isSubmitting,
    createPayment,
    cancelPayment,
    clearPaymentState,
    failPayment,
    requestFormSubmission,
    submitPayment,
    startPolling,
  } = props;

  // ─── Stripe ────────────────────────────────────────────────────────────────

  const { embeddedPaymentElementView, confirmPayment, isConfirmingPayment, isUpdatingTotal } =
    useGiftCardStripeIntegration({
      amount,
      form,
      createPayment,
      cancelPayment,
      clearPaymentState,
      failPayment,
      paymentMethods,
      requestFormSubmission,
      startPolling,
    });

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const onSubmitPress = useCallback(() => {
    return submitPayment(confirmPayment);
  }, [confirmPayment, submitPayment]);

  // ───────────────────────────────────────────────────────────────────────────

  const { match } = useResponsive();

  return (
    <View style={match([null, styles.containerSM, styles.containerMD])}>
      <GiftCardCheckoutFormContainer>
        <GiftCardCheckoutAmount sectionTitle={sectionTitles?.amount} form={form} rules={rules} />

        <GiftCardCheckoutDetails sectionTitle={sectionTitles?.details} form={form} rules={rules} />

        <GiftCardCheckoutDeliveryDate
          sectionTitle={sectionTitles?.deliveryDate}
          form={form}
          rules={rules}
        />

        <GiftCardCheckoutDeliveryMethod
          sectionTitle={sectionTitles?.deliveryMethod}
          sectionTitleEmailOnly={sectionTitles?.recipientEmail}
          form={form}
        />

        <GiftCardCheckoutPaymentElement embeddedPaymentElementView={embeddedPaymentElementView} />
      </GiftCardCheckoutFormContainer>

      <GiftCardCheckoutLedger form={form} />

      <GiftCardCheckoutSubmit
        isDisabled={!canSubmit || isUpdatingTotal}
        isSubmitting={isSubmitting || isConfirmingPayment}
        onPress={onSubmitPress}
      />
    </View>
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerSM: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
  },
  containerMD: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'flex-start',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardCheckoutFormContentProps = {
  amount: number;
  paymentMethods: readonly PaymentMethod[];
  sectionTitles?: SectionTitles;
  form: GiftCardCheckoutFormReturn;
  rules: GiftCardCheckoutFormFieldRules;
  canSubmit: boolean;
  isSubmitting: boolean;
  createPayment?: (paymentState: PaymentState) => void;
  cancelPayment?: () => void;
  clearPaymentState?: () => void;
  failPayment: () => void;
  startPolling?: (orderId: string) => void;
  requestFormSubmission: (paymentMethodId: string) => Promise<void>;
  submitPayment: (confirmPayment: () => Promise<void>) => Promise<void>;
};

type SectionTitles = {
  amount: string;
  details: string;
  deliveryDate: string;
  deliveryMethod: string;
  recipientEmail: string;
  paymentMethod: string;
};
